import { descendFilterPath, FILTER_ABSENT } from '../../filter/services/filter-dsl.compiler';
import { FORBIDDEN_PATH_SEGMENTS, NODE_KEY_PATTERN } from '../constants/flow.constants';
import type { IFlowNodeContext } from '../interfaces/node-context.interface';

export const TEMPLATE_ROOTS = ['trigger', 'event', 'item', 'loop', 'nodes'] as const;
// `vars` exists only inside a Library prompt (§16): the step binds each name in its own flow.
// `tool` only in an agent tool's fields: what the model filled in (agents §7.1).
export type TTemplateRoot = (typeof TEMPLATE_ROOTS)[number] | 'vars' | 'tool';

export interface IContextPath {
  path: string;
  root: TTemplateRoot;
  nodeKey: string | null;
}

export type TTemplatePart = { kind: 'text'; text: string } | { kind: 'ref'; ref: IContextPath };

export interface IParsedTemplate {
  parts: TTemplatePart[];
  /** The whole string is one `{{ path }}`: the value keeps its type instead of becoming text. */
  single: boolean;
}

export interface ITemplateRefAt extends IContextPath {
  field: string;
}

export interface ITemplateProblem {
  field: string;
  message: string;
}

export class FlowTemplateError extends Error {}

const SEGMENT = /^[A-Za-z0-9_$-]+$/;
const OPEN = '{{';
const CLOSE = '}}';
const ESCAPE = '\\';

export function parseContextPath(
  raw: string,
  roots: readonly TTemplateRoot[] = TEMPLATE_ROOTS,
): IContextPath {
  const path = raw.trim();
  const segments = path.split('.');
  const root = segments[0] as TTemplateRoot;
  if (!roots.includes(root)) {
    throw new FlowTemplateError(
      `"${path}" must start with one of ${roots.map((r) => `${r}.`).join(', ')}`,
    );
  }
  if (segments.some((segment) => !SEGMENT.test(segment))) {
    // Anything beyond a path runs on every event outside the sandbox (§5.3), so it is refused.
    throw new FlowTemplateError(
      `"${path}" is not a plain path — only a path goes inside {{ }}; anything more is a Code step`,
    );
  }
  const forbidden = segments.find((segment) => FORBIDDEN_PATH_SEGMENTS.includes(segment));
  if (forbidden) throw new FlowTemplateError(`"${path}": "${forbidden}" is not a field name`);
  if (root !== 'nodes') return { path, root, nodeKey: null };
  const nodeKey = segments[1];
  if (!nodeKey || !NODE_KEY_PATTERN.test(nodeKey)) {
    throw new FlowTemplateError(`"${path}" must name a step: nodes.<key>.…`);
  }
  return { path, root, nodeKey };
}

// Dedupe's old spelling (`body.id`, `id`, `headers.x`, `query.x`, `path`) in the one grammar.
export function canonicalKeyPath(raw: string): string {
  const path = raw.trim();
  const [head, ...rest] = path.split('.');
  if ((TEMPLATE_ROOTS as readonly string[]).includes(head)) return path;
  const tail = rest.join('.');
  switch (head) {
    case 'body':
      return tail ? `event.${tail}` : 'event';
    case 'headers':
      return tail ? `trigger.headers.${tail.toLowerCase()}` : 'trigger.headers';
    case 'query':
      return tail ? `trigger.query.${tail}` : 'trigger.query';
    case 'path':
      return rest.length === 0 ? 'trigger.path' : `event.${path}`;
    default:
      return path ? `event.${path}` : path;
  }
}

/** `\{{` is a literal `{{`. */
export function parseTemplate(
  text: string,
  roots: readonly TTemplateRoot[] = TEMPLATE_ROOTS,
): IParsedTemplate {
  const parts: TTemplatePart[] = [];
  let literal = '';
  let cursor = 0;
  while (cursor < text.length) {
    const open = text.indexOf(OPEN, cursor);
    if (open === -1) {
      literal += text.slice(cursor);
      break;
    }
    if (open > cursor && text[open - 1] === ESCAPE) {
      literal += `${text.slice(cursor, open - 1)}${OPEN}`;
      cursor = open + OPEN.length;
      continue;
    }
    literal += text.slice(cursor, open);
    const close = text.indexOf(CLOSE, open + OPEN.length);
    if (close === -1) throw new FlowTemplateError(`"{{" at ${open} is never closed with "}}"`);
    const inner = text.slice(open + OPEN.length, close);
    if (inner.trim() === '') throw new FlowTemplateError(`empty {{ }} at ${open}`);
    if (inner.includes(OPEN)) throw new FlowTemplateError(`"{{" inside {{ }} at ${open}`);
    if (literal !== '') parts.push({ kind: 'text', text: literal });
    literal = '';
    parts.push({ kind: 'ref', ref: parseContextPath(inner, roots) });
    cursor = close + CLOSE.length;
  }
  if (literal !== '') parts.push({ kind: 'text', text: literal });
  return { parts, single: parts.length === 1 && parts[0].kind === 'ref' };
}

function hasTemplate(text: string): boolean {
  return text.includes(OPEN);
}

/** What a template reads: a step's context, or a Library prompt's `{ event, trigger, vars }`. */
export type TTemplateContext =
  IFlowNodeContext | { event: unknown; trigger: unknown; vars: Record<string, unknown> };

/** `FILTER_ABSENT` when the path is not there, so `null` stays a real value. */
export function resolveContextPath(ctx: TTemplateContext, raw: string | IContextPath): unknown {
  const parsed = typeof raw === 'string' ? parseContextPath(raw) : raw;
  const segments = parsed.path.split('.');
  const scope = ctx as Partial<Record<TTemplateRoot, unknown>>;
  if (parsed.root === 'nodes') {
    const nodes = scope.nodes as Record<string, unknown> | undefined;
    const key = parsed.nodeKey as string;
    if (!nodes || !Object.hasOwn(nodes, key)) return FILTER_ABSENT;
    return descendFilterPath(nodes[key], segments.slice(2).join('.'));
  }
  const base = Object.hasOwn(scope, parsed.root) ? scope[parsed.root] : undefined;
  if (base === null || base === undefined) return FILTER_ABSENT;
  return descendFilterPath(base, segments.slice(1).join('.'));
}

export function renderTemplate(
  text: string,
  ctx: TTemplateContext,
  roots: readonly TTemplateRoot[] = TEMPLATE_ROOTS,
): unknown {
  if (!hasTemplate(text)) return text;
  const parsed = parseTemplate(text, roots);
  if (parsed.single) {
    const value = resolveContextPath(ctx, (parsed.parts[0] as { ref: IContextPath }).ref);
    return value === FILTER_ABSENT ? undefined : value;
  }
  return parsed.parts
    .map((part) => (part.kind === 'text' ? part.text : asText(resolveContextPath(ctx, part.ref))))
    .join('');
}

export function renderTemplates(
  config: Record<string, unknown>,
  fields: readonly string[],
  ctx: IFlowNodeContext,
): Record<string, unknown> {
  const rendered = structuredClone(config);
  for (const pattern of fields) {
    visitField(rendered, pattern.split('.'), '', (text, _at, set) =>
      set(renderTemplate(text, ctx)),
    );
  }
  return rendered;
}

export function asText(value: unknown): string {
  if (value === FILTER_ABSENT || value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

// Template fields only (`fields.*.value`, `input`: `*` is any key, an object is walked
// whole). Anywhere else `{{` is a literal, as in a filter value "Hello {{name}}".
export function collectTemplateRefs(
  config: unknown,
  fields: readonly string[],
  roots: readonly TTemplateRoot[] = TEMPLATE_ROOTS,
): { refs: ITemplateRefAt[]; problems: ITemplateProblem[] } {
  const refs: ITemplateRefAt[] = [];
  const problems: ITemplateProblem[] = [];
  const seen = new Set<string>();
  for (const pattern of fields) {
    visitField(config, pattern.split('.'), '', (text, at) => {
      if (!hasTemplate(text)) return;
      try {
        for (const part of parseTemplate(text, roots).parts) {
          if (part.kind !== 'ref' || seen.has(`${at}\u0000${part.ref.path}`)) continue;
          seen.add(`${at}\u0000${part.ref.path}`);
          refs.push({ ...part.ref, field: at });
        }
      } catch (error) {
        problems.push({
          field: at,
          message: error instanceof Error ? error.message : String(error),
        });
      }
    });
  }
  return { refs, problems };
}

type TVisit = (text: string, at: string, set: (value: unknown) => void) => void;

function visitField(value: unknown, pattern: string[], at: string, visit: TVisit): void {
  if (pattern.length === 0) {
    walkStrings(value, at, visit);
    return;
  }
  if (value === null || typeof value !== 'object') return;
  const [head, ...rest] = pattern;
  const container = value as Record<string, unknown>;
  const keys = head === '*' ? Object.keys(container) : [head];
  for (const key of keys) {
    if (!Object.hasOwn(container, key)) continue;
    const child = container[key];
    const path = at ? `${at}.${key}` : key;
    if (rest.length === 0 && typeof child === 'string') {
      visit(child, path, (next) => (container[key] = next));
    } else {
      visitField(child, rest, path, visit);
    }
  }
}

function walkStrings(value: unknown, at: string, visit: TVisit): void {
  if (value === null || typeof value !== 'object') return;
  const container = value as Record<string, unknown>;
  for (const key of Object.keys(container)) {
    const child = container[key];
    const path = at ? `${at}.${key}` : key;
    if (typeof child === 'string') visit(child, path, (next) => (container[key] = next));
    else walkStrings(child, path, visit);
  }
}
